import { useQuery } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { isPendingClerkTenantLink } from '@/hooks/useLinkPendingClerkTenant';
import { Workspace } from '@/models/WorkspacesByTenant';
import { useAuth } from '@/modules/Auth/hooks/useAuth';

import queryKeys from '../../modules/shared/query/queryKeys';

/** Backend returns a JSON array; legacy Gateway may wrap in { data }. */
function normalizeWorkspacesPayload(body: unknown): Workspace[] {
  if (Array.isArray(body)) {
    return body as Workspace[];
  }
  const o = body as { data?: Workspace[] };
  if (Array.isArray(o?.data)) {
    return o.data;
  }
  return [];
}

async function getWorkspacesByTenant(sessionJwt?: string | null) {
  const base = getBackendBaseUrl();
  const headers = backendRequestHeaders(sessionJwt);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token: set REACT_APP_DEV_JWT for local API.');
  }
  const response = await axios.get<unknown>(`${base}/workspaces`, { headers });
  return normalizeWorkspacesPayload(response.data);
}

type UseGetWorkspacesByTenantOptions = {
  /** When false, skips the query (e.g. until Path B tenant link completes). */
  enabled?: boolean;
};

export function useGetWorkspacesByTenant(options: UseGetWorkspacesByTenantOptions = {}) {
  const { user } = useAuth();
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const hasBearer = Boolean(resolveBearerToken(sessionJwt));
  const { enabled: enabledOverride = true } = options;
  const blockedByPendingLink = isPendingClerkTenantLink();

  return useQuery<Workspace[]>({
    queryKey: [queryKeys.workspaces, hasBearer, blockedByPendingLink],
    queryFn: () => getWorkspacesByTenant(sessionJwt),
    enabled: enabledOverride && Boolean(user && hasBearer) && !blockedByPendingLink,
    refetchOnWindowFocus: false,
  });
}
